// aiIntent.service.ts — narrowing the chatbot's OPEN string vocabularies.
//
// ─── WHY PARSERS AND NOT `z.enum` ──────────────────────────────────────────
// `intent`, `confidence` and `terminated_by` are `z.string()` on the wire, and
// `schemas/chat.schema.ts` explains at length why they must stay that way: a
// closed enum blanks the operator's entire panel — with an `ApiError
// { kind: 'validation' }`, not a graceful gap — the first time the chatbot team
// ships a new value. The same rule and the same reasoning already govern the GMO
// surface (`module/marketops/schemas/webhook-outbox.schema.ts`).
//
// So narrowing happens HERE, downstream of validation, and it is total: every
// unrecognised value returns `undefined` rather than throwing. `undefined` is
// not an error state — it is the instruction to the caller to take its
// conservative branch. `services/trust.service.ts` is where that branch lives:
// an unparseable `intent` gets the neutral `plain` bubble, which claims nothing
// about a reply we could not classify.
//
// NOTE on `parseAiConfidence` and `parseAiTerminatedBy`: neither has a
// production caller today. `terminated_by` never had one, and `confidence` lost
// its last one when the verify caveat was removed on operator request. They are
// kept on purpose — this file narrows the host's whole open vocabulary, not just
// the parts currently rendered, and `confidence` is still a REQUIRED field on
// the wire. Deleting them would mean re-deriving the same roster the next time a
// consumer appears. Do not render `confidence` if one does: see
// `services/trust.service.ts` for why that value is wrong in both directions.
//
// ─── WHY A ROSTER OBJECT AND NOT A `switch` ────────────────────────────────
// A `switch (value) { case 'data_question': return value; … }` would be shorter
// and equally type-safe. It would also keep compiling, silently, if someone
// added a member to `AiIntent` in `types.ts` and forgot this file — and the
// symptom would be a legal intent being treated as unrecognised, i.e. a reply
// quietly demoted to the conservative branch. `Readonly<Record<AiIntent, true>>`
// makes that omission a build error instead.

import type { AiConfidence, AiIntent, AiTerminatedBy } from '../types';

/**
 * Membership test against a roster keyed by the narrowed union.
 *
 * `Object.hasOwn` and not `in`: `in` walks the prototype chain, so `'toString'`
 * and `'constructor'` would parse as legal vocabulary values.
 */
function isRosterKey<K extends string>(
  roster: Readonly<Record<K, true>>,
  value: string,
): value is K {
  return Object.hasOwn(roster, value);
}

/**
 * Every `intent` this UI has a presentation for.
 *
 * Keyed by `AiIntent` so extending that union without extending this roster
 * fails the build. See the file header.
 */
const KNOWN_INTENTS: Readonly<Record<AiIntent, true>> = {
  data_question: true,
  chit_chat: true,
  out_of_scope: true,
  ambiguous: true,
};

/**
 * Every `confidence` value this UI recognises.
 *
 * Recognising a value is NOT the same as rendering it. Nothing in this module
 * ever prints a confidence word — see `types.ts`. The only thing `low` is
 * allowed to do is strengthen an existing warning.
 */
const KNOWN_CONFIDENCES: Readonly<Record<AiConfidence, true>> = {
  high: true,
  medium: true,
  low: true,
};

/** Every guard the server reports short-circuiting on. */
const KNOWN_TERMINATED_BY: Readonly<Record<AiTerminatedBy, true>> = {
  guard_chit_chat: true,
  guard_out_of_scope: true,
  guard_ambiguous: true,
};

/**
 * Narrow the server's `intent` string, or `undefined` if we do not know it.
 *
 * `undefined` means "present the reply conservatively", never "this reply is
 * broken". The reply itself is still perfectly displayable.
 */
export function parseAiIntent(value: string): AiIntent | undefined {
  return isRosterKey(KNOWN_INTENTS, value) ? value : undefined;
}

/**
 * Narrow the server's `confidence` string, or `undefined` if we do not know it.
 *
 * An unrecognised confidence must NOT weaken anything: callers treat
 * `undefined` exactly as they treat `medium`/`high` — the standard warning
 * stays. Only an explicit `low` strengthens it.
 */
export function parseAiConfidence(value: string): AiConfidence | undefined {
  return isRosterKey(KNOWN_CONFIDENCES, value) ? value : undefined;
}

/**
 * Narrow `terminated_by`, which is absent in normal replies, `null` when no
 * guard fired, and a diagnostic field that vanishes entirely when the server's
 * `CHAT_INCLUDE_DIAGNOSTICS` flag is off.
 *
 * All three of those collapse to `undefined` here on purpose: "no guard fired",
 * "we were not told", and "we do not recognise the guard" are indistinguishable
 * to the UI, and none of them changes how a reply is presented — `intent` does
 * that. This exists for the role-gated diagnostics disclosure, which is the only
 * place a guard name is worth showing.
 */
export function parseTerminatedBy(value: string | null | undefined): AiTerminatedBy | undefined {
  if (typeof value !== 'string') return undefined;
  return isRosterKey(KNOWN_TERMINATED_BY, value) ? value : undefined;
}
